import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { queryClient } from '../../../../../services/MaggieQueryClient';
import {
  createKPITemplate,
  deleteKPITemplate,
  fetchKPITemplates,
  updateKPITemplate,
} from '../../../../../services/queries/KPITemplatesQueries';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { IConsolidatedTemplate, isConsolidatedTemplate } from '../../../../../schemas/kpi-template.schema';
import { kpiTemplatesListStateJ } from '../../../../../services/state/KPI/KPITemplatesStateJ';
import { IKPITemplate } from '../../../../../data-models/kpi-template.data-model';

export const ConsolidatedTemplatesQueryKey = ['consolidated-templates'] as const;

export function useConsolidatedTemplatesState({ includeDeleted = false } = {}) {
  return useQuery({
    queryKey: ConsolidatedTemplatesQueryKey,
    queryFn: fetchKPITemplates,
    select: (templates) => {
      const consolidated = templates.filter(isConsolidatedTemplate);
      return includeDeleted ? consolidated : consolidated.filter((t) => !t.isDeleted);
    },
  });
}

/**
 * Temp bridge: keeps the React Query consolidated templates cache and the
 * Jotai kpiTemplatesListStateJ in sync so any template mutation (from either
 * stack) refreshes both. Drop once the React Query cache is the single
 * source of truth for templates.
 */
export function useInvalidateKPITemplates() {
  const setTemplates = useSetAtom(kpiTemplatesListStateJ);
  const { pushErrorToast } = useToastMessageState();

  return useCallback(async () => {
    try {
      queryClient.invalidateQueries({ queryKey: ConsolidatedTemplatesQueryKey });
      const templates = (await fetchKPITemplates()) as IKPITemplate[];
      setTemplates(templates);
    } catch (err) {
      Sentry.captureException(err);
      pushErrorToast({
        message: getErrorMessage(err, 'Failed to refresh KPI templates. Please reload the page.'),
      });
    }
  }, [pushErrorToast, setTemplates]);
}

export function useCreateConsolidatedTemplate() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateTemplates = useInvalidateKPITemplates();

  return useMutation({
    mutationFn: (template: Partial<IConsolidatedTemplate>) =>
      createKPITemplate(template) as Promise<IConsolidatedTemplate>,
    onSuccess: (_data, variables) => {
      invalidateTemplates();
      pushSuccessToast({ message: `Template "${variables.name}" created successfully.` });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({ message: getErrorMessage(error, 'Failed to create template.') });
    },
  });
}

export function useUpdateConsolidatedTemplate() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateTemplates = useInvalidateKPITemplates();

  return useMutation({
    mutationFn: (template: Partial<IConsolidatedTemplate>) =>
      updateKPITemplate(template) as Promise<IConsolidatedTemplate>,
    onSuccess: (_data, variables) => {
      invalidateTemplates();
      pushSuccessToast({ message: `Template "${variables.name}" updated successfully.` });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({ message: getErrorMessage(error, 'Failed to update template.') });
    },
  });
}

export function useDeleteConsolidatedTemplate() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateTemplates = useInvalidateKPITemplates();

  return useMutation({
    mutationFn: (templateId: number) => deleteKPITemplate(templateId),
    onSuccess: (_data, templateId) => {
      // Synchronously evict the deleted template from the cache so any reader
      // (e.g. CustomKpiTemplateRedirect) immediately sees the updated list and
      // does not redirect back to the just-deleted template. invalidateTemplates
      // still refetches server truth + syncs the Jotai bridge afterwards.
      queryClient.setQueryData<(IConsolidatedTemplate | IKPITemplate)[]>(
        ConsolidatedTemplatesQueryKey,
        (old) => old?.filter((t) => t.id !== templateId)
      );
      invalidateTemplates();
      pushSuccessToast({ message: 'Template deleted successfully.' });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({ message: getErrorMessage(error, 'Failed to delete template.') });
    },
  });
}
